import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import StarterWorkflowWizard from "../StarterWorkflowWizard";

// ─── Types ─────────────────────────────────────────────────────────────────────

interface KlaviyoStatus {
  connected: boolean;
  apiKeyMasked: string;
  accountName: string;
  eventCount: number;
  lastEventAt: string | null;
}

interface KlaviyoEventTemplate {
  id: string;
  name: string;
  trigger: string;
  description: string;
}

interface EventSetting {
  eventId: string;
  isActive: boolean;
  flowId: string;
}

// ─── Suggested events (5 templates from API, also matches server-side) ─────────

const DEFAULT_EVENTS: KlaviyoEventTemplate[] = [
  {
    id: "post_chat",
    name: "Post Chat Follow-up",
    trigger: "conversation_resolved",
    description:
      "Send a follow-up email after every resolved chat. Great for NPS, CSAT, and product recommendations."
  },
  {
    id: "csat_survey",
    name: "CSAT Survey",
    trigger: "conversation_resolved_csat",
    description:
      "Only fires when no CSAT score was collected in-chat. Sends a 1-question email survey."
  },
  {
    id: "abandoned_chat",
    name: "Abandoned Chat Recovery",
    trigger: "conversation_abandoned",
    description:
      "Fires when visitor leaves without getting an answer. Offers to pick up where they left off."
  },
  {
    id: "bot_escalation",
    name: "Escalation Notification",
    trigger: "bot_escalated_to_human",
    description:
      "When the bot can't answer and escalates to a human — send a 'we got your request' holding email."
  },
  {
    id: "order_followup",
    name: "Post-Purchase Bot Flow",
    trigger: "shopify_order_confirmed",
    description:
      "After a Shopify order closes in chat — trigger the post-purchase Klaviyo flow."
  }
];

// ─── Helper: chevron svg ────────────────────────────────────────────────────────

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`}
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M8 10.5L3 5.5H13L8 10.5Z" />
    </svg>
  );
}

// ─── FindKeyAccordion ────────────────────────────────────────────────────────────

function FindKeyAccordion() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden mt-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left bg-slate-50 hover:bg-slate-100 text-sm font-medium text-slate-700"
        aria-expanded={open}
      >
        <span>Where to find your Private API Key</span>
        <Chevron open={open} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-3 text-sm text-slate-600 space-y-1.5 bg-white border-t border-slate-100">
          <ol className="list-none space-y-2">
            {[
              "Log into your Klaviyo account at app.klaviyo.com",
              "Click your account name (top-left) → Settings → API Keys",
              "Click \"Create Private API Key\"",
              "Name it \"Velaro Integration\"",
              "Set Permissions to Full Access",
              "Click Create → Copy the key"
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
          <a
            href="https://developers.klaviyo.com/en/reference/api_overview"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs mt-2"
            aria-label="Klaviyo API documentation (opens in new tab)"
          >
            Klaviyo API docs
            <svg viewBox="0 0 12 12" width="11" height="11" fill="none" aria-hidden="true">
              <path d="M1 11L11 1M11 1H4M11 1V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </a>
        </div>
      )}
    </div>
  );
}

// ─── FlowTipAccordion ────────────────────────────────────────────────────────────

function FlowTipAccordion({ eventName }: { eventName: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-slate-100 overflow-hidden mt-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left bg-slate-50 hover:bg-slate-100 text-xs font-medium text-slate-600"
        aria-expanded={open}
      >
        <span>How to connect this to a Klaviyo Flow</span>
        <Chevron open={open} />
      </button>
      {open && (
        <div className="px-3 pb-3 pt-2.5 text-xs text-slate-600 space-y-1.5 bg-white border-t border-slate-100">
          <ol className="list-none space-y-1.5">
            {[
              "In Klaviyo, go to Flows → Create Flow",
              "Choose \"Build your own\"",
              "Set the trigger to \"API\" (Metric trigger)",
              `Use the metric name "Velaro ${eventName}"`,
              "Build your email sequence (e.g. send email 1 hour after trigger)",
              "Publish the flow",
              "Copy the Flow ID from the URL (e.g. ...flows/ABCD1234/...) and paste it in the field above"
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="flex-shrink-0 w-4 h-4 rounded-full bg-slate-200 text-slate-600 text-[9px] font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

// ─── EventCard ────────────────────────────────────────────────────────────────

interface EventCardProps {
  template: KlaviyoEventTemplate;
  setting: EventSetting;
  connected: boolean;
  onChange: (updated: EventSetting) => void;
}

function EventCard({ template, setting, connected, onChange }: EventCardProps) {
  return (
    <div
      className={`rounded-xl border p-4 transition-colors ${
        setting.isActive
          ? "border-blue-200 bg-blue-50/30"
          : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800">{template.name}</span>
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-mono">
              {template.trigger}
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">{template.description}</p>
        </div>

        {/* Toggle */}
        <button
          type="button"
          role="switch"
          aria-checked={setting.isActive}
          aria-label={`${setting.isActive ? "Disable" : "Enable"} ${template.name}`}
          disabled={!connected}
          onClick={() => onChange({ ...setting, isActive: !setting.isActive })}
          className={`relative flex-shrink-0 w-10 h-5 rounded-full transition-colors ${
            setting.isActive ? "bg-blue-600" : "bg-slate-300"
          } ${!connected ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
              setting.isActive ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      </div>

      {/* Flow ID input — only shown when active */}
      {setting.isActive && (
        <div className="mt-3">
          <label
            htmlFor={`flow-${template.id}`}
            className="block text-xs font-medium text-slate-600 mb-1"
          >
            Klaviyo Flow ID
            <span className="text-slate-400 font-normal ml-1">(paste from your Klaviyo flow URL)</span>
          </label>
          <input
            id={`flow-${template.id}`}
            type="text"
            value={setting.flowId}
            onChange={(e) => onChange({ ...setting, flowId: e.target.value })}
            placeholder="e.g. ABCD1234"
            className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
          />
          <FlowTipAccordion eventName={template.name} />
        </div>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function Klaviyo() {
  const api = useApi();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<KlaviyoStatus | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [saveResult, setSaveResult] = useState<{ success: boolean; message: string } | null>(null);
  const [eventSettings, setEventSettings] = useState<EventSetting[]>([]);

  // ── Load status + event settings ───────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [statusResp, settingsResp] = await Promise.all([
        api.admin.get("Klaviyo/Status"),
        api.admin.get("Klaviyo/EventSettings")
      ]);

      const statusData: KlaviyoStatus = await statusResp.json();
      setStatus(statusData);

      const savedSettings: EventSetting[] = Array.isArray(await settingsResp.json()) ? await settingsResp.json() : [];

      // Merge saved settings with defaults (fill in any missing event ids)
      const merged = DEFAULT_EVENTS.map((tmpl) => {
        const saved = savedSettings.find((s) => s.eventId === tmpl.id);
        return saved ?? { eventId: tmpl.id, isActive: false, flowId: "" };
      });
      setEventSettings(merged);
    } catch {
      setStatus({ connected: false, apiKeyMasked: "", accountName: "", eventCount: 0, lastEventAt: null });
      setEventSettings(DEFAULT_EVENTS.map((t) => ({ eventId: t.id, isActive: false, flowId: "" })));
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  // Reload settings after the first fetch (settingsResp was consumed above — refetch properly)
  const loadProper = useCallback(async () => {
    setLoading(true);
    try {
      const statusResp = await api.admin.get("Klaviyo/Status");
      const statusData: KlaviyoStatus = await statusResp.json();
      setStatus(statusData ?? { connected: false, apiKeyMasked: "", accountName: "", eventCount: 0, lastEventAt: null });

      const settingsResp = await api.admin.get("Klaviyo/EventSettings");
      const savedSettings: EventSetting[] = (() => {
        try { return settingsResp.ok ? [] : []; } catch { return []; }
      })();

      // Properly read settings
      const settingsData = await settingsResp.json().catch(() => []);
      const savedArr = Array.isArray(settingsData) ? settingsData : [];

      const merged = DEFAULT_EVENTS.map((tmpl) => {
        const saved = savedArr.find((s: EventSetting) => s.eventId === tmpl.id);
        return saved ?? { eventId: tmpl.id, isActive: false, flowId: "" };
      });
      setEventSettings(merged);
    } catch {
      setStatus({ connected: false, apiKeyMasked: "", accountName: "", eventCount: 0, lastEventAt: null });
      setEventSettings(DEFAULT_EVENTS.map((t) => ({ eventId: t.id, isActive: false, flowId: "" })));
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => { loadProper(); }, [loadProper]);

  // ── Save & Test ─────────────────────────────────────────────────────────────

  async function handleSaveAndTest() {
    setSaving(true);
    setTestResult(null);
    setSaveResult(null);
    try {
      // 1. Save config (key + event settings)
      const saveResp = await api.admin.post("Klaviyo/SaveConfig", {
        privateApiKey: apiKey.trim() || undefined,
        eventSettings: eventSettings
      });
      const saveData = await saveResp.json();

      if (!saveData?.success) {
        setSaveResult({ success: false, message: saveData?.error ?? "Failed to save." });
        return;
      }
      setSaveResult({ success: true, message: "Configuration saved." });

      // 2. Test connection
      setTesting(true);
      const testResp = await api.admin.post("Klaviyo/TestConnection", {
        privateApiKey: apiKey.trim() || undefined
      });
      const testData = await testResp.json();

      if (testData?.success) {
        setTestResult({
          success: true,
          message: `Connected${testData.accountName ? ` — ${testData.accountName}` : ""}.`
        });
        setApiKey(""); // clear the input — masked key will show from status
        await loadProper();
      } else {
        setTestResult({
          success: false,
          message: testData?.error ?? "Connection test failed. Check your API key."
        });
      }
    } catch {
      setSaveResult({ success: false, message: "Network error — check your connection and try again." });
    } finally {
      setSaving(false);
      setTesting(false);
    }
  }

  async function handleSaveEvents() {
    setSaving(true);
    setSaveResult(null);
    try {
      const resp = await api.admin.post("Klaviyo/SaveConfig", {
        eventSettings: eventSettings
      });
      const data = await resp.json();
      setSaveResult({
        success: data?.success ?? false,
        message: data?.success ? "Event settings saved." : (data?.error ?? "Failed to save.")
      });
    } catch {
      setSaveResult({ success: false, message: "Network error." });
    } finally {
      setSaving(false);
    }
  }

  function handleEventChange(updated: EventSetting) {
    setEventSettings((prev) =>
      prev.map((s) => (s.eventId === updated.eventId ? updated : s))
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  const connected = status?.connected ?? false;

  return (
    <div className="space-y-5 max-w-2xl">

      {/* ── Section 1: Connect ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Connect Klaviyo</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Trigger email and SMS flows after Velaro conversations resolve.
            </p>
          </div>
          <span
            className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
              connected
                ? "bg-green-100 text-green-700"
                : "bg-slate-100 text-slate-500"
            }`}
          >
            {connected ? "Connected" : "Not connected"}
          </span>
        </div>

        {/* Connected state: show account + masked key */}
        {connected && (
          <div className="rounded-lg bg-green-50 border border-green-200 p-3 mb-4 text-sm text-green-800 flex items-center gap-2">
            <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
              <circle cx="8" cy="8" r="7" fill="#16a34a" />
              <path d="M4 8l3 3 5-5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>
              {status?.accountName
                ? <>Connected to <strong>{status.accountName}</strong></>
                : "Connected"}
              {status?.apiKeyMasked && (
                <span className="ml-2 text-green-700 font-mono text-xs">{status.apiKeyMasked}</span>
              )}
            </span>
          </div>
        )}

        {/* API Key input */}
        <div className="space-y-3">
          <div>
            <label htmlFor="klaviyo-api-key" className="block text-sm font-medium text-slate-700 mb-1">
              Private API Key
              {connected && (
                <span className="text-slate-400 font-normal ml-1">
                  (leave blank to keep current key)
                </span>
              )}
            </label>
            <input
              id="klaviyo-api-key"
              type="password"
              value={apiKey}
              onChange={(e) => { setApiKey(e.target.value); setTestResult(null); setSaveResult(null); }}
              placeholder="pk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              autoComplete="new-password"
              className="w-full text-sm px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white font-mono"
            />
            <FindKeyAccordion />
          </div>

          {/* Result banners */}
          {saveResult && (
            <div
              className={`rounded-lg p-3 text-sm ${
                saveResult.success
                  ? "bg-green-50 border border-green-200 text-green-800"
                  : "bg-red-50 border border-red-200 text-red-800"
              }`}
              role="status"
            >
              {saveResult.message}
            </div>
          )}
          {testResult && (
            <div
              className={`rounded-lg p-3 text-sm ${
                testResult.success
                  ? "bg-green-50 border border-green-200 text-green-800"
                  : "bg-red-50 border border-red-200 text-red-800"
              }`}
              role="status"
            >
              {testResult.message}
            </div>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={handleSaveAndTest}
              disabled={saving || testing || (!apiKey && !connected)}
              className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${
                saving || testing || (!apiKey && !connected)
                  ? "bg-slate-300 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {saving ? "Saving…" : testing ? "Testing…" : "Save & Test Connection"}
            </button>
            <a
              href="https://developers.klaviyo.com/en/reference/api_overview"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline"
              aria-label="Klaviyo developer documentation (opens in new tab)"
            >
              Klaviyo docs
            </a>
          </div>
        </div>

        {/* Usage stats */}
        {connected && (status?.eventCount ?? 0) > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-6 text-xs text-slate-500">
            <span>
              <strong className="text-slate-700">{status?.eventCount?.toLocaleString()}</strong> events fired
            </span>
            {status?.lastEventAt && (
              <span>
                Last event: <strong className="text-slate-700">{new Date(status.lastEventAt).toLocaleString()}</strong>
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Deploy callout ── */}
      {connected && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-blue-800 mb-0.5">
                Deploy the chat widget to your site
              </div>
              <div className="text-xs text-blue-600">
                Your Klaviyo integration is connected — grab your embed code from Deployments to go live.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a href="/chat-windows" className="text-xs px-3 py-1.5 rounded-lg bg-white border border-blue-300 text-blue-700 hover:bg-blue-100 transition">Chat Window</a>
              <a href="/deployments" className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition">Get Embed Code →</a>
            </div>
          </div>
          <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
            <i className="fa-solid fa-lightbulb mt-0.5 shrink-0" />
            <span>
              <strong>Tip:</strong> You can also create a <strong>Knowledge Base Q&A</strong> page widget
              from <a href="/deployments" className="underline hover:text-blue-900">Deployments → Page Widgets</a> for
              a lightweight, AI-only Q&A experience — no live agent needed.
            </span>
          </div>
        </div>
      )}

      {/* ── Section 2: Events ──────────────────────────────────────────────── */}
      {connected && (
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="flex items-start justify-between mb-1">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">Event Triggers</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Choose which Velaro events fire Klaviyo flows. Toggle active and paste your Klaviyo Flow ID.
              </p>
            </div>
            <button
              type="button"
              onClick={handleSaveEvents}
              disabled={saving}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium text-white transition-colors flex-shrink-0 ${
                saving ? "bg-slate-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {saving ? "Saving…" : "Save Events"}
            </button>
          </div>

          {saveResult && (
            <div
              className={`rounded-lg p-3 text-sm mb-4 ${
                saveResult.success
                  ? "bg-green-50 border border-green-200 text-green-800"
                  : "bg-red-50 border border-red-200 text-red-800"
              }`}
              role="status"
            >
              {saveResult.message}
            </div>
          )}

          <div className="space-y-3 mt-4">
            {DEFAULT_EVENTS.map((tmpl) => {
              const setting = eventSettings.find((s) => s.eventId === tmpl.id)
                ?? { eventId: tmpl.id, isActive: false, flowId: "" };
              return (
                <EventCard
                  key={tmpl.id}
                  template={tmpl}
                  setting={setting}
                  connected={connected}
                  onChange={handleEventChange}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* ── Section 3: AI Tools ────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-1">AI Bot Tools</h2>
        <p className="text-sm text-slate-500 mb-4">
          6 tools activate immediately when Klaviyo is connected — no extra setup required.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            {
              name: "klaviyo_get_lists",
              label: "Get Lists",
              desc: "Discover all Klaviyo lists and their IDs. Use before subscribing a customer when the list ID is unknown."
            },
            {
              name: "klaviyo_add_to_list",
              label: "Add to List",
              desc: "Subscribe a customer email to a Klaviyo list in real time. Creates or upserts the profile automatically."
            },
            {
              name: "klaviyo_remove_from_list",
              label: "Remove from List",
              desc: "Unsubscribe a customer from a specific list. Use for compliance requests or preference changes."
            },
            {
              name: "klaviyo_track_event",
              label: "Track Event",
              desc: "Fire a custom Klaviyo event (e.g. 'High Purchase Intent') — appears in flows and analytics within seconds."
            },
            {
              name: "klaviyo_get_profile",
              label: "Get Profile",
              desc: "Look up a subscriber's Klaviyo profile by email — list memberships, custom attributes, and created date."
            },
            {
              name: "klaviyo_update_profile",
              label: "Update Profile",
              desc: "Set custom attributes mid-chat (vip_tier, product_interest, chat_source) on a subscriber's profile."
            }
          ].map((tool) => (
            <div key={tool.name} className="rounded-xl border border-slate-200 p-3 bg-slate-50">
              <div className="text-xs font-mono text-blue-700 bg-blue-50 border border-blue-100 rounded px-2 py-0.5 inline-block mb-2 break-all">
                {tool.name}
              </div>
              <div className="text-sm font-semibold text-slate-800 mb-1">{tool.label}</div>
              <div className="text-xs text-slate-500">{tool.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Section 4: How it works ────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4">How it works</h2>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {[
            { num: "1", label: "Visitor chats with your bot", icon: "💬" },
            { num: "2", label: "Conversation resolves → Velaro fires a Klaviyo event with visitor email + conversation summary", icon: "⚡" },
            { num: "3", label: "Klaviyo sends your pre-built email flow automatically", icon: "📧" }
          ].map((step, i) => (
            <React.Fragment key={step.num}>
              <div className="flex items-start gap-3 flex-1">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center">
                  {step.num}
                </span>
                <div>
                  <span className="text-lg" aria-hidden="true">{step.icon}</span>
                  <p className="text-sm text-slate-700 mt-0.5">{step.label}</p>
                </div>
              </div>
              {i < 2 && (
                <svg
                  className="hidden sm:block flex-shrink-0 text-slate-300 rotate-0"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M7 3l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
                </svg>
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="mt-5 pt-4 border-t border-slate-100 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
          <strong className="text-slate-700">Velaro sends to Klaviyo:</strong> visitor email, first name, conversation
          summary, channel (chat / SMS / WhatsApp), CSAT score (if collected), and any conversation variables extracted
          during the chat.
        </div>
      </div>

      {/* ── Section 5: Starter Wizard ──────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Workflow Templates</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Launch a pre-built Klaviyo workflow in seconds — post-chat follow-ups, cart recovery, and more.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowWizard(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#6B46C1] hover:bg-[#5a38a8] transition-colors flex-shrink-0"
          >
            <i className="fa-solid fa-wand-magic-sparkles text-xs" aria-hidden="true" />
            Launch Starter Wizard
          </button>
        </div>
      </div>

      {showWizard && (
        <StarterWorkflowWizard
          integration="klaviyo"
          onClose={() => setShowWizard(false)}
        />
      )}
    </div>
  );
}

export function BackToIntegrationLandingKlaviyo() {
  return (
    <button
      type="button"
      onClick={() => { window.location.href = "/integrations"; }}
      className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
      aria-label="Back to integrations"
    >
      <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
        <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      Integrations
    </button>
  );
}
